/* ──────────────────────────────────────────────────────────────────────
 *  FellowMemberCard — discovery card for "Fellow members of {Org}"
 *  (HANDOFF_FEED_MEMBER_DISCOVERY, Slice C).
 *
 *  Consumes the shared FlipCard primitive (DEV-1440) for the flip shell +
 *  interaction parity (hover / tap / keyboard-focus / reduced-motion), and
 *  supplies only its distinct skin:
 *    Front — avatar + binary trust ring + name + org basis + flip hint.
 *    Back  — stripped Impact mini-radar (or fallback) + ring label +
 *            Request-as-contact (primary) + Follow (secondary) + dismiss row.
 *
 *  The only selection signal is shared org membership — functional, not
 *  social (CLAUDE.md). ImpactSignatureMini is radar-only (no descriptions /
 *  recommendations / roles), satisfying DEV-517.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiImpactSignature } from '../../api/useApiImpactSignature';
import { sendContactRequest } from '../../api/chat';
import { followEntity } from '../../api/follows';
import { mediaSrc } from '../../api/mediaUrl';
import type { FellowMemberCandidate } from '../../api/discovery';
import FlipCard from '../FlipCard/FlipCard';
import ImpactSignatureMini from '../DimensionRadar/ImpactSignatureMini';
import styles from './FellowMemberCard.module.css';

export interface FellowMemberCardProps {
  candidate: FellowMemberCandidate;
  /** Called after a snooze/ignore so the parent can remove the card. */
  onDismiss?: (targetGuid: string, kind: 'snooze' | 'ignore') => void;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

export default function FellowMemberCard({ candidate, onDismiss }: FellowMemberCardProps) {
  const { t } = useAppTranslation('userProfile');
  const navigate = useNavigate();

  // Prefer the full name; fall back to display name when it's blank.
  const fullName = candidate.name?.trim() || candidate.displayName;

  const [requested, setRequested] = useState(false);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  const { tiers } = useApiImpactSignature(candidate.targetGuid);

  const handleRequest = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (busy || requested) return;
      setBusy(true);
      try {
        await sendContactRequest(candidate.targetGuid);
        setRequested(true);
      } catch {
        /* surfaced by the global API error handler */
      } finally {
        setBusy(false);
      }
    },
    [busy, requested, candidate.targetGuid],
  );

  const handleFollow = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (busy || following) return;
      setBusy(true);
      try {
        await followEntity('user', candidate.targetGuid);
        setFollowing(true);
      } catch {
        /* surfaced by the global API error handler */
      } finally {
        setBusy(false);
      }
    },
    [busy, following, candidate.targetGuid],
  );

  const handleViewProfile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/profile/${candidate.targetGuid}`);
    },
    [navigate, candidate.targetGuid],
  );

  const handleDismiss = useCallback(
    (e: React.MouseEvent, kind: 'snooze' | 'ignore') => {
      e.stopPropagation();
      onDismiss?.(candidate.targetGuid, kind);
    },
    [onDismiss, candidate.targetGuid],
  );

  const orgBasis = t('discoveryCard.orgBasis', { org: candidate.orgName });
  const ringLabel = candidate.ringActive
    ? t('discoveryCard.ringActive')
    : t('discoveryCard.ringPassive');

  const photo = candidate.profileImageUrl ? mediaSrc(candidate.profileImageUrl) : null;
  const front = (
    <div className={styles.frontFace}>
      <div
        className={`${styles.avatar} ${candidate.ringActive ? styles.avatarBlue : styles.avatarNeutral}`}
      >
        {photo ? (
          <img src={photo} alt="" className={styles.avatarImg} />
        ) : (
          <span className={styles.avatarInitials}>{initialsOf(fullName)}</span>
        )}
      </div>
      <div className={styles.frontName}>{fullName}</div>
      <div className={styles.frontOrg}>{orgBasis}</div>
    </div>
  );

  const back = (
    <div className={styles.backContent}>
      <span className={styles.backName}>{fullName}</span>
      <span className={styles.ringLabel}>{ringLabel}</span>

      <div className={styles.radarWrap}>
        {tiers ? (
          <ImpactSignatureMini tiers={tiers} />
        ) : (
          <span className={styles.noImpact}>{t('discoveryCard.impactEmpty')}</span>
        )}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={handleRequest}
          disabled={busy || requested}
        >
          {requested ? t('discoveryCard.requested') : t('discoveryCard.requestContact')}
        </button>
        <div className={styles.secondaryRow}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={handleFollow}
            disabled={busy || following}
          >
            {following ? t('discoveryCard.following') : t('discoveryCard.follow')}
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={handleViewProfile}>
            {t('discoveryCard.viewProfile')}
          </button>
        </div>
      </div>

      <div className={styles.dismissRow}>
        <button type="button" className={styles.dismissBtn} onClick={(e) => handleDismiss(e, 'snooze')}>
          {t('discoveryCard.notNow')}
        </button>
        <button type="button" className={styles.dismissBtn} onClick={(e) => handleDismiss(e, 'ignore')}>
          {t('discoveryCard.notInterested')}
        </button>
      </div>
    </div>
  );

  return <FlipCard front={front} back={back} className={styles.card} ariaLabel={orgBasis} />;
}
